import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
    ClientEntity,
    CompanyController,
    DocGenPdfStatusEnum,
    DocumentConfigPartial,
    DocumentGenericField,
    DocumentItemConfigsPartial,
    DocumentItemPartial,
    DocumentItemTypeEnum,
    DocumentStatusEnum,
    SignedByPartial,
    UserController,
    UserEntity,
} from '@domain';
import { environment } from '@environments';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { DocumentSectionController } from 'src/app/domain/controllers/document/document-section.controller';
import { DocumentEntity } from 'src/app/domain/entities/document.entity';
import { DocumentCoverPartial } from 'src/app/domain/entities/partials/documents/document-cover.partial';
import { DocumentResponsiblePartial } from 'src/app/domain/entities/partials/documents/document-responsible.partial';
import {
    DocumentSessionChecklistPartial,
    DocumentSessionFocusedPartial,
    DocumentSessionPartial,
} from 'src/app/domain/entities/partials/documents/document-session.partial';
import { OnlineUserPartial } from 'src/app/domain/entities/partials/online-user.partial';
import { DocumentSessionTypeEnum } from 'src/app/domain/enums/documents/document-session-type.enum';

/** Pedido de foco vindo da prévia: qual card e se a edição do texto abre junto. */
export interface ItemFocusRequest {
    itemId: string;
    edit: boolean;
}

/**
 * Tom de um realce na prévia (modo "Editar com IA"): `running` pulsa em azul enquanto a IA
 * mexe; `done` é o flash que esmaece; `conflict` (laranja) e `error` (vermelho tracejado)
 * ficam até o usuário decidir ou tentar de novo.
 */
export type PreviewHighlightTone = 'running' | 'done' | 'conflict' | 'error';

export interface PreviewHighlightMark {
    itemIds: string[];
    tone: PreviewHighlightTone;
}

/** Realce de itens na prévia. `marks` são realces extras, com outro tom, mantidos junto. */
export interface PreviewHighlight extends PreviewHighlightMark {
    marks?: PreviewHighlightMark[];
}

/** Id especial aceito por `requestPreviewScroll`: rola a prévia até o topo (a capa). */
export const PREVIEW_TOP_ITEM = '__preview-top__';

@Injectable({
    providedIn: 'root',
})
export class EditDataService {
    // * Dados privados para serem acessados apenas por métodos internos
    private _document = signal<DocumentEntity>(null);
    private _client = signal<ClientEntity>(null);
    private _currentSession = signal<DocumentSessionPartial>(null);
    private _currentSessionIsDisabled = signal<boolean>(false);
    private _selectedItems = signal<DocumentItemPartial[]>([]);

    // * Pasta (CHECKLIST_FOLDER) aberta na lista de itens — `null` = nível seção.
    // O fluxo de criação carimba `parentFolderId` nos novos campos com esse valor.
    private _currentFolderId = signal<string | null>(null);

    // * Dados publics para refleitir os dados privados
    public document = this._document.asReadonly();
    public client = this._client.asReadonly();
    public currentSession = this._currentSession.asReadonly();
    public currentSessionIsDisabled = this._currentSessionIsDisabled.asReadonly();
    public selectedItems = this._selectedItems.asReadonly();
    public currentFolderId = this._currentFolderId.asReadonly();

    // * Flag de UI: painel lateral de prévia do PDF (inicia fechado)
    private _pdfPreviewOpen = signal(false);
    public pdfPreviewOpen = this._pdfPreviewOpen.asReadonly();
    public togglePdfPreviewOpen = (): void => this._pdfPreviewOpen.update(value => !value);
    /** Abre a prévia sem alternar: usado por quem PRECISA dela na tela (ver requestPreviewScroll). */
    public openPdfPreview = (): void => this._pdfPreviewOpen.set(true);
    /** Fecha a prévia sem alternar: o painel da mídia a tira da coluna (MediaLookPanelService). */
    public closePdfPreview = (): void => this._pdfPreviewOpen.set(false);

    /**
     * Painel de formato da mídia (Foto, Grade, Vídeo) na coluna da direita. Quem abre e fecha
     * é o MediaLookPanelService; aqui fica só o que o resto do editor precisa saber.
     */
    private _mediaPanelOpen = signal(false);
    public mediaPanelOpen = this._mediaPanelOpen.asReadonly();
    public setMediaPanelOpen = (open: boolean): void => this._mediaPanelOpen.set(open);

    // * Flag de UI: lista de seções minimizada/expandida (inicia aberta)
    private _sessionsCollapsed = signal(false);
    /**
     * Trava a lista FECHADA, sem toggle e SEM NAVEGAÇÃO.
     *
     * Serve à aba do ORÇAMENTO: as seções listadas ali são as do CONTEÚDO, então
     * deixá-las à vista (e clicáveis) enquanto o usuário edita itens/taxas leva a
     * editar a coisa errada. A trava é de UI e some ao voltar para "Conteúdo".
     *
     * Note o "sem navegação": é o que separa esta trava de `sessionsForcedClosed`.
     */
    private _sessionsLocked = signal(false);
    public sessionsLocked = this._sessionsLocked.asReadonly();
    public setSessionsLocked = (locked: boolean): void => this._sessionsLocked.set(locked);

    /**
     * Lista fechada e sem o botão de expandir. Duas razões:
     *
     * - aba do ORÇAMENTO (ver `_sessionsLocked`);
     * - prévia do PDF aberta: os três painéis dividiriam a largura e o editor sobraria
     *   com uma coluna estreita. Entre lista e editor, quem encolhe é a lista — o editor
     *   é o que o usuário está usando. Vale em QUALQUER largura, sem medir: a prévia é
     *   uma escolha de "quero ver o resultado", e nesse momento a lista não é o foco.
     *   O painel de formato da mídia vale o mesmo, e ainda segura a lista fechada durante
     *   a troca com a prévia (senão ela abria e fechava no meio da animação).
     *
     * Fechada NÃO é inerte: no trilho estreito os cards continuam clicáveis e trocam de
     * seção. Só a trava do orçamento bloqueia a navegação, e por um motivo próprio (a
     * seção aberta dali seria a de CONTEÚDO).
     */
    public sessionsForcedClosed = computed(
        () => this._sessionsLocked() || this._pdfPreviewOpen() || this._mediaPanelOpen()
    );
    public sessionsCollapsed = computed(
        () => this.sessionsForcedClosed() || this._sessionsCollapsed()
    );
    public toggleSessionsCollapsed = (): void => {
        if (this.sessionsForcedClosed()) return;
        this._sessionsCollapsed.update(value => !value);
    };

    // * Prévia expandida: a folha A4 na largura padrão fica pequena para ler o texto;
    // expandida ela ocupa a maior parte da tela sem esconder o editor. Já abre expandida
    // (pedido do usuário, 2026-09-25); o botão da prévia recolhe.
    private _pdfPreviewExpanded = signal(true);
    public pdfPreviewExpanded = this._pdfPreviewExpanded.asReadonly();
    public togglePdfPreviewExpanded = (): void => this._pdfPreviewExpanded.update(value => !value);

    /**
     * Onde o PDF quebra de página, na régua dos ITENS do editor.
     *
     * Quem preenche é o painel de prévia, com a paginação real do paged.js (por isso vale
     * para quebra por seção, item PAGE_BREAK e quebra por altura, sem heurística nenhuma).
     * A prévia roda desde a abertura do editor, mesmo com o painel fechado, então a lista
     * costuma estar pronta antes de o usuário pedir qualquer coisa.
     *
     * - `pageStartByItemId`: item que ABRE a folha (o traço aparece acima dele);
     * - `continuesByItemId`: item que apenas CONTINUA de uma folha anterior (a quebra caiu
     *   no meio dele; o card só avisa que segue na página seguinte).
     */
    /**
     * A prévia está remontando o documento ou repaginando.
     *
     * Vale mesmo com o painel fechado, porque a paginação roda em segundo plano para
     * manter os traços de quebra em dia: sem este sinal o usuário edita, os traços somem
     * por um instante e nada explica o porquê.
     */
    private _pdfPreviewBusy = signal(false);
    public pdfPreviewBusy = this._pdfPreviewBusy.asReadonly();
    public setPdfPreviewBusy = (busy: boolean): void => this._pdfPreviewBusy.set(busy);

    private _pdfPageStartByItemId = signal<Record<string, number>>({});
    private _pdfContinuesByItemId = signal<Record<string, number>>({});
    public pdfPageStartByItemId = this._pdfPageStartByItemId.asReadonly();
    public pdfContinuesByItemId = this._pdfContinuesByItemId.asReadonly();

    /**
     * Tabelas que atravessam folhas: por item, em qual linha IMPRESSA (0 = a primeira que
     * sai no PDF; linhas vazias não contam) cada folha nova começa. O card da tabela
     * desenha o traço "Página N" entre as linhas.
     */
    private _pdfRowBreaksByItemId = signal<Record<string, { row: number; page: number }[]>>({});
    public pdfRowBreaksByItemId = this._pdfRowBreaksByItemId.asReadonly();

    public setPdfPageBreaks(
        startsPage: { itemId: string; page: number }[],
        continuesInto: { itemId: string; page: number }[],
        rowBreaks: { itemId: string; page: number; row: number }[] = []
    ): void {
        const starts: Record<string, number> = {};
        // A folha 1 não tem quebra "antes" dela: é o começo do documento.
        for (const entry of startsPage) if (entry.page > 1) starts[entry.itemId] = entry.page;

        const continues: Record<string, number> = {};
        for (const entry of continuesInto) continues[entry.itemId] = entry.page;

        const rows: Record<string, { row: number; page: number }[]> = {};
        for (const entry of rowBreaks) {
            (rows[entry.itemId] ??= []).push({ row: entry.row, page: entry.page });
        }

        this._pdfPageStartByItemId.set(starts);
        this._pdfContinuesByItemId.set(continues);
        this._pdfRowBreaksByItemId.set(rows);
    }

    // * Indicador "Salvando… / Salvo": contador de saves em voo + flash de confirmação.
    // Todo save silencioso (config de item, data de imagem, texto, config do documento,
    // persistência de mídia) passa por beginSave/endSave para o header dar retorno.
    //
    // O estado é AMORTECIDO de propósito. Espelhar os saves crus fazia a pílula
    // piscar: um auto-save responde em ~150ms, então "Salvando…" aparecia por dois
    // frames e já era substituído por "Salvo" — e mexer em vários controles seguidos
    // virava um estrobo. Duas defesas:
    //  - carência: só anuncia "Salvando…" se o save passar de SAVING_GRACE_MS;
    //  - piso: uma vez anunciado, fica ao menos SAVING_MIN_MS antes de virar "Salvo".
    // Um save novo TAMBÉM não apaga o "Salvo" anterior: ele só renova a confirmação
    // quando termina, então a pílula nunca some entre duas edições seguidas.
    private static readonly SAVING_GRACE_MS = 400;
    private static readonly SAVING_MIN_MS = 600;
    private static readonly SAVED_DWELL_MS = 2500;

    private _pendingSaves = signal(0);
    private _saveStatus = signal<'idle' | 'saving' | 'saved'>('idle');
    private _graceTimer?: ReturnType<typeof setTimeout>;
    private _savedTimer?: ReturnType<typeof setTimeout>;
    private _savingShownAt = 0;

    /** Estado exibido pela pílula de save e pelo header (já amortecido). */
    public saveStatus = this._saveStatus.asReadonly();
    public isSaving = computed(() => this._saveStatus() === 'saving');
    public justSaved = computed(() => this._saveStatus() === 'saved');

    /**
     * Ecos realtime (`field-updated`) e refetches não podem sobrescrever o `configs`
     * local de um campo cujo save ainda está em voo — nem logo depois dele: o eco do
     * Pusher chega DEPOIS da resposta HTTP e devolveria um estado já superado,
     * fazendo o toggle "desvirar" sozinho. Daí a janela de tolerância pós-save.
     */
    private static readonly CONFIG_ECHO_COOLDOWN_MS = 4000;
    private _configSavesInFlight = new Map<string, number>();
    private _configSaveCooldownUntil = new Map<string, number>();

    public documentDeleted = signal(false); // * Flag para indicar se o documento foi deletado
    public documentIsBlocked = signal(false); // * Flag para indicar se o documento está bloqueado

    /**
     * Um save (auto-save silencioso) começou. Não muda a pílula na hora: agenda a
     * carência, e só um save realmente lento chega a anunciar "Salvando…".
     */
    public beginSave(): void {
        this._pendingSaves.update(count => count + 1);
        if (this._pendingSaves() > 1) return; // já há um ciclo em andamento

        clearTimeout(this._graceTimer);
        this._graceTimer = setTimeout(() => {
            if (this._pendingSaves() === 0) return;

            clearTimeout(this._savedTimer);
            this._savingShownAt = Date.now();
            this._saveStatus.set('saving');
        }, EditDataService.SAVING_GRACE_MS);
    }

    /**
     * Um save terminou. Quando é o último em voo e deu certo, mostra "Salvo" por
     * alguns segundos — a confirmação que faltava nos auto-saves silenciosos.
     */
    public endSave(succeeded = true): void {
        this._pendingSaves.update(count => Math.max(0, count - 1));
        if (this._pendingSaves() > 0) return;

        clearTimeout(this._graceTimer);

        if (!succeeded) {
            // A falha tem toast próprio; aqui a pílula apenas sai de cena.
            clearTimeout(this._savedTimer);
            this._saveStatus.set('idle');
            return;
        }

        // Se "Salvando…" chegou a aparecer, cumpre o tempo mínimo antes de virar
        // "Salvo" — senão as duas mensagens se atropelam.
        const elapsed =
            this._saveStatus() === 'saving' ? Date.now() - this._savingShownAt : Infinity;
        const wait = Math.max(0, EditDataService.SAVING_MIN_MS - elapsed);

        if (wait === 0) {
            this._showSaved();
            return;
        }

        clearTimeout(this._savedTimer);
        this._savedTimer = setTimeout(() => this._showSaved(), wait);
    }

    /** Confirma "Salvo" e agenda a volta ao repouso. */
    private _showSaved(): void {
        // Uma edição nova entrou em voo nesse meio-tempo: o ciclo dela é que decide.
        if (this._pendingSaves() > 0) return;

        this._saveStatus.set('saved');
        clearTimeout(this._savedTimer);
        this._savedTimer = setTimeout(() => {
            // Não recolhe a pílula por cima de um save que acabou de começar.
            if (this._pendingSaves() > 0) return;
            this._saveStatus.set('idle');
        }, EditDataService.SAVED_DWELL_MS);
    }

    /** O override de config do campo entrou em voo: ecos param de valer para ele. */
    public markFieldConfigSaving(fieldId: string): void {
        this._configSavesInFlight.set(fieldId, (this._configSavesInFlight.get(fieldId) ?? 0) + 1);
    }

    /** O save do override terminou: abre a janela de tolerância contra o eco atrasado. */
    public unmarkFieldConfigSaving(fieldId: string): void {
        const count = (this._configSavesInFlight.get(fieldId) ?? 1) - 1;
        if (count > 0) {
            this._configSavesInFlight.set(fieldId, count);
            return;
        }
        this._configSavesInFlight.delete(fieldId);
        this._configSaveCooldownUntil.set(
            fieldId,
            Date.now() + EditDataService.CONFIG_ECHO_COOLDOWN_MS
        );
    }

    /** `true` enquanto o `configs` local do campo é mais confiável que o de ecos/refetches. */
    private _shouldKeepLocalConfigs(fieldId: string): boolean {
        if (this._configSavesInFlight.has(fieldId)) return true;

        const until = this._configSaveCooldownUntil.get(fieldId);
        if (!until) return false;
        if (Date.now() <= until) return true;

        this._configSaveCooldownUntil.delete(fieldId);
        return false;
    }

    // * [Injects]
    private _userController = inject(UserController);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _documentSessionController = inject(DocumentSectionController);
    private _companyController = inject(CompanyController);

    /**
     * Fonte aplicada ao texto do documento na edição.
     * Prioridade: fonte do documento → fonte da empresa → padrão.
     */
    public documentFontFamily = computed(() => {
        const docFont = this._document()?.config?.fontFamily;
        if (docFont && docFont.trim()) return docFont.trim();

        const companyFont =
            this._companyController.currentCompany()?.documentConfig?.configs?.fontFamily;
        if (companyFont && companyFont.trim()) return companyFont.trim();

        return environment.DEFAULT_FONT_FAMILY;
    });

    /**
     * `true` se o usuário logado é responsável pelo documento. Só responsáveis podem
     * marcar/resetar seções no modo checklist.
     */
    public isCurrentUserResponsible = computed(() => {
        const userId = this._userController.getCurrentUserId();
        if (!userId) return false;
        return (this._document()?.responsibles ?? []).some(
            r => r.userId?.toString() === userId.toString()
        );
    });

    /**
     * Quem pode marcar/resetar o checklist: responsável OU criador OU owner/admin.
     * Espelha o gate do backend — cobre documentos antigos sem responsáveis, onde
     * ninguém conseguiria marcar (nem o owner).
     */
    public canCompleteChecklist = computed(() => {
        if (this.isCurrentUserResponsible()) return true;
        if (this._userController.isAdminOrOwner()) return true;
        const createdBy = this._document()?.createdBy;
        return !!createdBy && createdBy === this._userController.getCurrentUserId();
    });

    constructor() {
        effect(() => {
            this.documentIsBlocked.set(
                this._document()?.status == DocumentStatusEnum.PUBLISHED ||
                    this._document()?.status == DocumentStatusEnum.SIGNED
            );
        });
    }

    /**
     * Seta o cliente
     * @param client Cliente
     */
    setClient = (client: ClientEntity) => this._client.set(client);

    /**
     * Seta o documento
     * @param document Documento
     */
    public setDocument(document: DocumentEntity): void {
        this._document.set(document);

        // Se tiver documento continua
        if (!document) return;

        // Veficia se tem uma seção corrente
        if (this._currentSession()) {
            // Busca a seção corrente
            const section = document.sessions.find(x => x._id == this._currentSession()._id);
            if (!section) {
                // Seta a primeira seção como a seção atual
                this._currentSession.set(document.sessions[0]);

                return;
            }

            // Seta a seção corrente
            this._currentSession.set(section);
        } else if (document.sessions.length > 0) {
            this.setCurrentSession(document.sessions[0]);
        }

        // Verifica se a seção tem mídia não carregada
        this.checkMidiaNotLoaded();
    }

    /**
     * Aplica só a `config` do documento, preservando seções, seleção e navegação.
     * Usado pelo auto-save do modal de configurações: substituir o documento inteiro
     * pela resposta atropelava edições otimistas concorrentes (cards, textos).
     */
    public applyDocumentConfig(config: DocumentConfigPartial): void {
        this._document.update(doc => (doc ? { ...doc, config } : doc));
    }

    /**
     * Reflete a conclusão da geração de PDF no documento local (status COMPLETED + pdfURL),
     * sem disparar os efeitos colaterais de `setDocument`. Isso faz o cabeçalho trocar o
     * botão "Gerar PDF" por "Baixar PDF" assim que a geração termina (paridade com o preview).
     */
    public applyPdfGenerationResult(pdfURL: string): void {
        this._document.update(doc =>
            doc ? { ...doc, pdfStatus: DocGenPdfStatusEnum.COMPLETED, pdfURL } : doc
        );
    }

    /**
     * Marca o PDF pronto como desatualizado: o conteúdo do documento mudou, então
     * o cabeçalho volta a oferecer "Gerar PDF" em vez de baixar o arquivo antigo.
     */
    public invalidatePdf(): void {
        this._document.update(doc =>
            doc?.pdfStatus === DocGenPdfStatusEnum.COMPLETED
                ? { ...doc, pdfStatus: null, pdfURL: null }
                : doc
        );
    }

    /**
     * Limpa o documento e a seção atual
     */
    public clearDocument(): void {
        this._document.set(undefined);
        this._client.set(undefined);
        this._currentSession.set(undefined);
        this._previewHighlight.set(null);
        this.documentDeleted.set(false);
    }

    /**
     * Retorna o ID do documento
     * @returns ID do documento
     */
    public getDocumentId = () => this._document()._id;

    /**
     * Reordena os itens da seção atual
     * @param previousIndex Índice anterior do item
     * @param currentIndex Novo índice do item
     */
    public reorderItems(previousIndex: number, currentIndex: number): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this.documentIsBlocked()) return;

        const section = this._currentSession();
        if (!section || !section.items) return;

        // Guarda a ordem anterior para rollback em caso de falha
        const previousItems = section.items;

        const items = [...previousItems];
        const [movedItem] = items.splice(previousIndex, 1);
        items.splice(currentIndex, 0, movedItem);

        // Aplica a lista de itens sem mutar a seção existente
        // (novas referências para os signals emitirem corretamente)
        const applyItems = (nextItems: DocumentItemPartial[]) => {
            this._document.update(doc => {
                if (!doc) return doc;

                return {
                    ...doc,
                    sessions: doc.sessions.map(s =>
                        s._id === section._id ? { ...s, items: nextItems } : s
                    ),
                };
            });

            this._currentSession.update(current =>
                current && current._id === section._id ? { ...current, items: nextItems } : current
            );
        };

        // Aplica a nova ordem de forma otimista
        applyItems(items);

        // Reordena os campos da seção
        this._documentFieldsController
            .reorderFields(this.getDocumentId(), section._id, previousIndex, currentIndex)
            .subscribe({
                // Falha no servidor: desfaz a reordenação local para não divergir
                error: () => applyItems(previousItems),
            });
    }

    /**
     * Reordena as sessões do documento
     * @param previousIndex Índice anterior da seção
     * @param currentIndex Novo índice da seção
     */
    public reorderSessions(previousIndex: number, currentIndex: number): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this.documentIsBlocked()) return;

        const document = this._document();
        if (!document || !document.sessions) return;

        // Guarda a ordem anterior para rollback em caso de falha
        const previousSessions = document.sessions;

        const sessions = [...previousSessions];
        const [movedSession] = sessions.splice(previousIndex, 1);
        sessions.splice(currentIndex, 0, movedSession);

        const applySessions = (nextSessions: DocumentSessionPartial[]) => {
            this._document.update(doc => {
                if (!doc) return doc;

                return {
                    ...doc,
                    sessions: nextSessions,
                };
            });

            // Atualiza a seção atual se necessário
            const currentSession = this._currentSession();
            if (currentSession) {
                const updatedSession = nextSessions.find(s => s._id === currentSession._id);
                if (updatedSession) {
                    this._currentSession.set(updatedSession);
                }
            }
        };

        // Aplica a nova ordem de forma otimista
        applySessions(sessions);

        // Reordena as sessões
        this._documentSessionController
            .reorderSections(this.getDocumentId(), previousIndex, currentIndex)
            .subscribe({
                // Falha no servidor: desfaz a reordenação local para não divergir
                error: () => applySessions(previousSessions),
            });
    }

    private async checkMidiaNotLoaded(): Promise<void> {
        const currentDoc = this._document();
        const sessions = currentDoc.sessions;
        if (!sessions || sessions.length === 0) return;

        // Processa todas as sessões em paralelo e retorna um mapa com os resultados
        const sessionChecks = sessions.map(section =>
            this.checkSessionMedia(section).then(hasError => ({ sessionId: section._id, hasError }))
        );

        // Aguarda todas as verificações
        const results = await Promise.all(sessionChecks);

        // Cria um mapa de resultados por sessionId
        const errorMap = new Map<string, boolean>();
        results.forEach(result => {
            errorMap.set(result.sessionId, result.hasError);
        });

        // Atualiza o documento com os resultados
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.map(section => ({
                    ...section,
                    hasMediaNotLoaded: errorMap.get(section._id) || false,
                })),
            };
        });

        // Atualiza também a seção atual se necessário
        const currentSession = this._currentSession();
        if (currentSession) {
            const updatedDoc = this._document();
            const updatedSession = updatedDoc.sessions.find(s => s._id === currentSession._id);
            if (updatedSession) {
                this._currentSession.set(updatedSession);
            }
        }
    }

    private async checkSessionMedia(section: DocumentSessionPartial): Promise<boolean> {
        const items = section.items;
        if (!items || items.length === 0) return false;

        const mediaChecks: Promise<boolean>[] = [];

        for (const item of items) {
            if (
                item.type == DocumentItemTypeEnum.IMAGE ||
                item.type == DocumentItemTypeEnum.VIDEO
            ) {
                // Verifica se não tem URL válida em item.value
                if (!item.value || (typeof item.value === 'string' && item.value.trim() === '')) {
                    return true; // Erro encontrado
                }

                // Adiciona verificação assíncrona da mídia
                mediaChecks.push(this.checkMediaLoad(item.value, item.type));
            } else if (item.type == DocumentItemTypeEnum.GROUP_IMAGE) {
                // Verifica se item.value é uma lista válida
                if (!item.value || !Array.isArray(item.value) || item.value.length === 0) {
                    return true; // Erro encontrado
                }

                // Verifica cada imagem do grupo
                for (const imageItem of item.value) {
                    if (!imageItem?.url || imageItem.url.trim() === '') {
                        return true; // Erro encontrado
                    }

                    // Adiciona verificação assíncrona da mídia
                    mediaChecks.push(
                        this.checkMediaLoad(imageItem.url, DocumentItemTypeEnum.IMAGE)
                    );
                }
            }
        }

        // Aguarda todas as verificações e verifica se alguma falhou
        if (mediaChecks.length > 0) {
            const results = await Promise.all(mediaChecks);
            return results.some(hasError => hasError);
        }

        return false; // Sem erros
    }

    // Limita as verificações simultâneas para não estourar o limite de conexões do navegador
    private _mediaCheckActive = 0;
    private _mediaCheckWaiters: (() => void)[] = [];
    private static readonly MEDIA_CHECK_CONCURRENCY = 4;

    private async checkMediaLoad(url: string, type: DocumentItemTypeEnum): Promise<boolean> {
        if (this._mediaCheckActive >= EditDataService.MEDIA_CHECK_CONCURRENCY) {
            await new Promise<void>(resolve => this._mediaCheckWaiters.push(resolve));
        }
        this._mediaCheckActive++;

        try {
            return await this._loadMedia(url, type);
        } finally {
            this._mediaCheckActive--;
            const next = this._mediaCheckWaiters.shift();
            if (next) next();
        }
    }

    private _loadMedia(url: string, type: DocumentItemTypeEnum): Promise<boolean> {
        return new Promise(resolve => {
            let resolved = false;
            const TIMEOUT_MS = 15000;

            const timeout = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    // Timeout não significa mídia corrompida (pode ser rede lenta),
                    // então não marcamos como erro para evitar falso positivo
                    resolve(false);
                }
            }, TIMEOUT_MS);

            const handleSuccess = () => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    resolve(false); // Carregou com sucesso
                }
            };

            const handleError = () => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    resolve(true); // Erro ao carregar
                }
            };

            if (type === DocumentItemTypeEnum.IMAGE) {
                const img = new Image();
                img.onload = handleSuccess;
                img.onerror = handleError;
                img.src = url;
            } else if (type === DocumentItemTypeEnum.VIDEO) {
                const video = document.createElement('video');
                // Com preload 'metadata' o evento confiável é 'loadedmetadata';
                // 'loadeddata' pode nunca disparar e gerava falso warning por timeout
                video.onloadedmetadata = handleSuccess;
                video.onerror = handleError;
                video.preload = 'metadata';
                video.src = url;
                video.load();
            } else {
                clearTimeout(timeout);
                resolve(false);
            }
        });
    }

    // * [Sessões]

    /**
     * Seta a seção atual
     * @param section seção
     */
    public setCurrentSession(section: DocumentSessionPartial): void {
        const currentSessionId = this.getCurrentSessionId();
        if (currentSessionId == section._id) return;

        // Seta a seção atual
        this._currentSession.set(section);

        // Define o foco na seção
        if (!section.focused) {
            this._documentSessionController
                .setFocusInSection(this.getDocumentId(), section._id)
                .subscribe();
        } else {
            this._documentSessionController.removeFocusInSection(this.getDocumentId()).subscribe();
        }

        // Limpa os itens selecionados
        this.cleanSelectedItems();

        // Se o usuário focado na seção for diferente do usuário atual, define como desabilitado
        if (
            !section.focused ||
            section.focused?.userId == this._userController.getCurrentUserId()
        ) {
            this._currentSessionIsDisabled.set(false);
        } else {
            this._currentSessionIsDisabled.set(true);
        }
    }

    /**
     * Retorna todas as sessões do documento sem a seção atual
     * @returns Sessões do documento sem a seção atual
     */
    public getAllSessionsWithoutCurrent = () =>
        this._document()?.sessions.filter(section => section._id !== this._currentSession()?._id) ||
        [];

    /**
     * Retorna o ID da seção atual
     * @returns ID da seção atual
     */
    public getCurrentSessionId = () => this._currentSession()?._id;

    /**
     * Card que a prévia mandou trazer para a tela: a coluna de itens rola até ele, o destaca
     * e, se pedido, já abre a edição do texto. Quem consome (section-items) limpa o pedido
     * depois de atender — e é ele quem abre o editor, não o painel: o pedido costuma chegar
     * junto com a troca de seção, e a troca LIMPA o composer; abrindo depois de o card
     * existir na tela, a limpeza já passou.
     */
    private _focusRequest = signal<ItemFocusRequest | null>(null);
    public focusRequest = this._focusRequest.asReadonly();
    public requestItemFocus = (itemId: string, edit = false): void =>
        this._focusRequest.set({ itemId, edit });
    public clearItemFocus = (): void => this._focusRequest.set(null);

    /**
     * Item sob o mouse na PRÉVIA. O card correspondente se acende na coluna de itens (só
     * quando está na seção aberta, claro): "olha onde eu estou" nas duas telas ao mesmo tempo.
     */
    private _previewHoverItemId = signal<string | null>(null);
    public previewHoverItemId = this._previewHoverItemId.asReadonly();
    public setPreviewHoverItem = (itemId: string | null): void =>
        this._previewHoverItemId.set(itemId);

    /**
     * Card que deve abrir o painel de "configurações deste item" (pedido da prévia).
     *
     * O painel é aberto pelo botão do CARD (cada card tem o seu), então o pedido chega por
     * aqui e o card dono o abre sozinho. Ele mesmo limpa o pedido, senão voltar para a
     * seção depois reabriria o painel do nada.
     */
    private _itemConfigRequestId = signal<string | null>(null);
    public itemConfigRequestId = this._itemConfigRequestId.asReadonly();
    public requestItemConfig = (itemId: string): void => this._itemConfigRequestId.set(itemId);
    public clearItemConfigRequest = (): void => this._itemConfigRequestId.set(null);

    /**
     * Item que a PRÉVIA deve procurar e mostrar ("Encontrar no preview", no menu do card).
     *
     * É o caminho inverso dos três sinais acima: aqui quem pede é o editor e quem atende é o
     * painel do PDF, que repassa o pedido para dentro do iframe (a folha paginada mora lá, e
     * quem rola é a janela dele). O painel limpa o pedido ao atender.
     */
    /**
     * Itens realçados na PRÉVIA pelo modo "Editar com IA": `running` pulsa em azul enquanto
     * a IA mexe neles; `done` é o flash que esmaece quando a alteração termina. O painel do
     * PDF repassa ao iframe (e de novo a cada repaginação, que recria a folha).
     */
    private _previewHighlight = signal<PreviewHighlight | null>(null);
    public previewHighlight = this._previewHighlight.asReadonly();
    public setPreviewHighlight = (highlight: PreviewHighlight | null): void => {
        const any =
            !!highlight?.itemIds.length || !!highlight?.marks?.some(mark => mark.itemIds.length);
        this._previewHighlight.set(any ? highlight : null);
    };

    private _previewScrollRequestId = signal<string | null>(null);
    public previewScrollRequestId = this._previewScrollRequestId.asReadonly();
    /**
     * Pede à prévia para rolar até um item. `PREVIEW_TOP_ITEM` leva ao topo (a capa):
     * é o destino das alterações que não têm item, como capa e título do documento.
     */
    public requestPreviewScroll = (itemId: string): void =>
        this._previewScrollRequestId.set(itemId);
    public clearPreviewScrollRequest = (): void => this._previewScrollRequestId.set(null);

    /**
     * Acha um campo — e a seção DONA dele — em qualquer seção do documento.
     *
     * A prévia mostra o documento inteiro, não só a seção aberta: quando a ação nasce lá
     * (a seta de "começar na próxima página" ao lado de um título), o campo clicado pode
     * estar em outra seção, e o caminho por `currentSession` simplesmente não o acharia.
     */
    public findFieldAnywhere(
        fieldId: string | undefined
    ): { sessionId: string; field: DocumentItemPartial } | undefined {
        if (!fieldId) return undefined;

        for (const session of this._document()?.sessions ?? []) {
            const field = session.items?.find(item => item._id === fieldId);
            if (field) return { sessionId: session._id, field };
        }

        return undefined;
    }

    // * [Itens selecionados]

    /**
     * Adiciona um item selecionado
     * @param item Item
     */
    public addSelectedItem(item: DocumentItemPartial): void {
        this._selectedItems.set([...this._selectedItems(), item]);
    }

    /**
     * Remove um item selecionado
     * @param item Item
     */
    public removeSelectedItem(item: DocumentItemPartial): void {
        this._selectedItems.set(
            this._selectedItems().filter(selectedItem => selectedItem._id !== item._id)
        );
    }

    /**
     * Limpa todos os itens selecionados
     */
    public cleanSelectedItems = () => {
        this._selectedItems().forEach(item => (item.selected = false));
        this._selectedItems.set([]);
    };

    // #region // * [Updates - Document]

    public documentTitleUpdated(title: string): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                title: title,
            };
        });
    }

    /**
     * Atualiza o footer do documento (realtime ou após salvar).
     * @param footer Lista de campos do footer
     */
    public documentFooterUpdated(footer: DocumentGenericField[]): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                footer: footer,
            };
        });
    }

    /**
     * Atualiza o header do documento (realtime ou após salvar).
     * @param header Lista de campos do header
     */
    public documentHeaderUpdated(header: DocumentGenericField[]): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                header: header,
            };
        });
    }

    /**
     * Atualiza a capa do documento (realtime ou após salvar/trocar template).
     * @param cover Capa do documento
     */
    public documentCoverUpdated(cover: DocumentCoverPartial): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                cover: cover,
            };
        });
    }

    public documentOnlineUserUpdated(user: UserEntity, isOnline: boolean): void {
        if (!user || !user._id) return;

        if (isOnline) {
            const onlineUser: OnlineUserPartial = {
                _id: user._id,
                name: user.name,
                avatar: user.avatar,
            };

            this._document.update(doc => {
                if (!doc) return doc;

                const onlineUsers = doc.onlineUsers ?? [];
                if (onlineUsers.some(u => u._id === user._id)) return doc;

                return {
                    ...doc,
                    onlineUsers: [...onlineUsers, onlineUser],
                };
            });
        } else {
            this._document.update(doc => {
                if (!doc) return doc;

                return {
                    ...doc,
                    onlineUsers: (doc.onlineUsers ?? []).filter(u => u._id !== user._id),
                };
            });
        }
    }

    /**
     * Substitui a lista de usuários online sem tocar no restante do documento,
     * evitando sobrescrever eventos realtime aplicados durante o carregamento.
     */
    public documentOnlineUsersSet(onlineUsers: OnlineUserPartial[]): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                onlineUsers: onlineUsers ?? [],
            };
        });
    }

    public documentContractUpdated(status: DocumentStatusEnum, signedBy: SignedByPartial[]): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                status: status,
                signedBy: signedBy,
            };
        });
    }

    /**
     * Liga/desliga o modo checklist no documento em memória (realtime ou após salvar).
     */
    public documentChecklistModeUpdated(checklistMode: boolean): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                config: { ...(doc.config ?? {}), checklistMode } as DocumentConfigPartial,
            };
        });
    }

    /**
     * Atualiza a lista de responsáveis no documento em memória (realtime ou após salvar).
     */
    public documentResponsiblesUpdated(responsibles: DocumentResponsiblePartial[]): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                responsibles: responsibles ?? [],
            };
        });
    }

    /**
     * Reseta o checklist: desmarca todas as seções (realtime ou após salvar).
     */
    public documentChecklistReset(): void {
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.map(section => ({ ...section, checklist: { done: false } })),
            };
        });

        this._currentSession.update(section =>
            section ? { ...section, checklist: { done: false } } : section
        );
    }

    /**
     * Aplica o grid de imagens a TODOS os grupos do documento (realtime ou após salvar).
     * Espelha o backend: atualiza o default (`config`) e o `configs.groupImageGrid` de
     * cada item GROUP_IMAGE, preservando a seção atual do usuário.
     */
    public documentGroupImageGridApplied(grid: number): void {
        const applyToSession = (section: DocumentSessionPartial): DocumentSessionPartial => ({
            ...section,
            items: (section.items ?? []).map(item =>
                item.type === DocumentItemTypeEnum.GROUP_IMAGE
                    ? { ...item, configs: { ...(item.configs ?? {}), groupImageGrid: grid } }
                    : item
            ),
        });

        this._document.update(doc => {
            if (!doc) return doc;
            return {
                ...doc,
                config: { ...(doc.config ?? {}), groupImageGrid: grid } as DocumentConfigPartial,
                sessions: (doc.sessions ?? []).map(applyToSession),
            };
        });

        this._currentSession.update(section => (section ? applyToSession(section) : section));
    }

    // #endregion

    // #region // * [Updates - Sessions]

    private _updateSessionById(
        sessionId: string,
        updater: (section: DocumentSessionPartial) => DocumentSessionPartial
    ): void {
        if (!sessionId) return;

        // Atualiza a seção no documento
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.map(section =>
                    section._id === sessionId ? updater(section) : section
                ),
            };
        });

        // Atualiza a seção atual quando necessário
        this._currentSession.update(section => {
            if (!section || section._id !== sessionId) return section;
            return updater(section);
        });
    }

    /** Usuário logado (quem cria algo pelo editor e depois recebe o próprio eco). */
    public currentUserId(): string {
        return this._userController.getCurrentUserId();
    }

    public sessionCreated(section: DocumentSessionPartial, createdBy: string): void {
        // Atualiza o documento com a nova seção. Idempotente: o "Mover para nova seção"
        // aplica a seção com a resposta do servidor e o eco do realtime chega depois.
        this._document.update(doc => {
            if (!doc) return doc;
            if (doc.sessions.some(existing => existing._id === section._id)) return doc;

            // Adiciona a nova seção ao documento
            return {
                ...doc,
                sessions: [...doc.sessions, section],
            };
        });

        // Seta a seção atual se não houver uma seção atual
        if (!this._currentSession()) this.setCurrentSession(section);

        // Verifica se foi o usuário atual que criou e navega para a seção
        if (
            createdBy == this._userController.getCurrentUserId() &&
            section.type == DocumentSessionTypeEnum.NORMAL
        ) {
            this.setCurrentSession(section);
        }
    }

    /**
     * Atualiza o estado de checklist de uma seção (realtime ou após salvar).
     */
    public sessionChecklistUpdated(
        sessionId: string,
        checklist: DocumentSessionChecklistPartial
    ): void {
        this._updateSessionById(sessionId, section => ({ ...section, checklist }));
    }

    /** Define a pasta de checklist aberta (`null` = nível da seção). */
    public setCurrentFolderId(folderId: string | null): void {
        this._currentFolderId.set(folderId);
    }

    /** Renomeia um item (pasta de checklist) no documento + seção atual. */
    public folderRenamed(sessionId: string, folderId: string, name: string): void {
        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items ?? []).map(item =>
                item._id === folderId ? { ...item, name } : item
            ),
        }));
    }

    /**
     * Atualiza o checklist de uma PASTA (item) e o check derivado da seção, em
     * ambos os signals (documento + seção atual). Usado no marcar otimista, na
     * resposta do servidor e no realtime — sem substituir o documento inteiro
     * (que deixaria a `currentSession` obsoleta na tela de itens).
     */
    public folderChecklistUpdated(
        sessionId: string,
        itemId: string,
        checklist: DocumentSessionChecklistPartial,
        sessionChecklist: DocumentSessionChecklistPartial
    ): void {
        this._updateSessionById(sessionId, section => ({
            ...section,
            checklist: sessionChecklist,
            items: (section.items ?? []).map(item =>
                item._id === itemId ? { ...item, checklist } : item
            ),
        }));
    }

    /**
     * Atualiza a descrição de uma seção (realtime ou após salvar).
     */
    public sessionDescriptionUpdated(description: string, sessionId: string): void {
        this._updateSessionById(sessionId, section => ({ ...section, description }));
    }

    public sessionNameUpdated(name: string, sessionId: string): void {
        // Atualiza o nome da seção no documento
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.map(section =>
                    section._id == sessionId ? { ...section, name: name } : section
                ),
            };
        });

        // Atualiza o nome da seção na seção atual
        this._currentSession.update(section => {
            if (!section || section._id != sessionId) return section;

            return {
                ...section,
                name: name,
            };
        });
    }

    public sessionsDeleted(sessionsListIds: string[]): void {
        const currentSessionId = this._currentSession()?._id;

        // Atualiza o documento com as sessões removidas
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.filter(section => !sessionsListIds.includes(section._id)),
            };
        });

        // Seta a seção atual se a seção atual foi removida
        if (currentSessionId && sessionsListIds.includes(currentSessionId)) {
            const updatedDoc = this._document();
            const nextSession = updatedDoc?.sessions?.[0];

            if (nextSession) {
                this.setCurrentSession(nextSession);
            } else {
                this._currentSession.set(undefined);
                this.cleanSelectedItems();
            }
        }
    }

    public sessionSetFocusUpdated(sessionId: string, focused: DocumentSessionFocusedPartial): void {
        // Atualiza o foco da seção no documento
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.map(section =>
                    section._id == sessionId ? { ...section, focused: focused } : section
                ),
            };
        });

        // Atualiza o foco da seção na seção atual
        this._currentSession.update(section => {
            if (!section || section._id != sessionId) return section;

            return {
                ...section,
                focused: focused,
            };
        });
    }

    public sessionRemoveFocusUpdated(sessionId: string): void {
        // Atualiza o foco da seção no documento
        this._document.update(doc => {
            if (!doc) return doc;

            return {
                ...doc,
                sessions: doc.sessions.map(section =>
                    section._id == sessionId ? { ...section, focused: undefined } : section
                ),
            };
        });

        // Atualiza o foco da seção na seção atual
        this._currentSession.update(section => {
            if (!section || section._id != sessionId) return section;

            return {
                ...section,
                focused: undefined,
            };
        });

        // Se a seção atual for igual aqui, setamos o usuário corrente como focado
        if (sessionId == this._currentSession()?._id) {
            this._documentSessionController
                .setFocusInSection(this.getDocumentId(), sessionId)
                .subscribe();
        }
    }

    public sessionReordered(sessionsIds: string[]): void {
        // Atualiza o documento com as sessões reordenadas
        this._document.update(doc => {
            if (!doc) return doc;

            // Mapeia os IDs para as sessões correspondentes, filtrando undefined
            const reorderedSessions = sessionsIds
                .map(sessionId => doc.sessions.find(section => section._id === sessionId))
                .filter((section): section is DocumentSessionPartial => section !== undefined);

            // Se alguma seção não foi encontrada, mantém a ordem atual
            if (reorderedSessions.length !== sessionsIds.length) {
                console.warn('Algumas sessões não foram encontradas durante a reordenação');
                return doc;
            }

            return {
                ...doc,
                sessions: reorderedSessions,
            };
        });

        // Atualiza a seção atual se necessário
        const currentSession = this._currentSession();
        if (currentSession) {
            const updatedDoc = this._document();
            const updatedSession = updatedDoc?.sessions.find(s => s._id === currentSession._id);
            if (updatedSession) {
                this._currentSession.set(updatedSession);
            }
        }
    }

    // #endregion

    // #region // * [Updates - Fields]

    public fieldsCreated(
        fields: { value: DocumentItemPartial; index: number }[],
        sessionSize: number,
        sessionId?: string
    ): void {
        const targetSessionId = sessionId ?? this._currentSession()?._id;
        if (!targetSessionId) return;

        this._updateSessionById(targetSessionId, section => {
            const currentItems = [...(section.items || [])];
            const normalizedFields = [...fields].sort(
                (first, second) => (first.index ?? 0) - (second.index ?? 0)
            );

            return {
                ...section,
                size: sessionSize,
                items: normalizedFields.reduce((items, field) => {
                    const insertIndex =
                        Number.isFinite(field.index) && field.index >= 0
                            ? Math.min(field.index, items.length)
                            : items.length;
                    items.splice(insertIndex, 0, field.value);
                    return items;
                }, currentItems),
            };
        });
    }

    /**
     * Aplica localmente o override de configuração de UM campo (imagem/grupo).
     *
     * Substitui o objeto inteiro (não faz merge): o modal já envia o override completo
     * que deve valer para o card, e limpar uma opção significa remover a chave para
     * voltar a herdar a config do documento.
     */
    public fieldConfigsUpdated(fieldId: string, configs: DocumentItemConfigsPartial): void {
        // A seção dona do campo, e não a que está aberta: a prévia edita o documento
        // inteiro, então o campo pode estar em qualquer seção.
        const sessionId = this.findFieldAnywhere(fieldId)?.sessionId ?? this._currentSession()?._id;
        if (!sessionId || !fieldId) return;

        // Config é um clique: a prévia pode repaginar já, sem esperar o silêncio da digitação.
        this._discretePreviewChange = true;

        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items || []).map(item =>
                item._id === fieldId ? { ...item, configs } : item
            ),
        }));
    }

    /**
     * Aplica localmente o `value` de um campo (lista da grade de indicadores) e, quando
     * vier, a revisão nova. Imutável, como as configs, para a prévia enxergar a mudança.
     */
    public fieldValueUpdated(fieldId: string, value: unknown, rev?: number): void {
        const sessionId = this.findFieldAnywhere(fieldId)?.sessionId ?? this._currentSession()?._id;
        if (!sessionId || !fieldId) return;

        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items || []).map(item =>
                item._id === fieldId ? { ...item, value, ...(rev != null ? { rev } : {}) } : item
            ),
        }));
    }

    /** Guarda a revisão devolvida pelo servidor numa escrita que não passou pelo realtime. */
    /**
     * Texto de um campo gravado por fora do editor inline (ex.: a fonte do documento mudou
     * e a fonte própria dos trechos saiu): troca o objeto do item para os cards e a prévia
     * re-renderizarem, e já carimba a revisão nova.
     */
    public fieldTextUpdated(
        fieldId: string,
        target: 'value' | 'description',
        text: string,
        rev?: number
    ): void {
        const sessionId = this.findFieldAnywhere(fieldId)?.sessionId;
        if (!sessionId) return;

        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items || []).map(item =>
                item._id === fieldId ? { ...item, [target]: text, rev: rev ?? item.rev } : item
            ),
        }));
    }

    public fieldRevUpdated(fieldId: string, rev: number): void {
        const sessionId = this.findFieldAnywhere(fieldId)?.sessionId ?? this._currentSession()?._id;
        if (!sessionId || !fieldId) return;

        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items || []).map(item =>
                item._id === fieldId ? { ...item, rev } : item
            ),
        }));
    }

    /**
     * A prévia repagina depois de um silêncio de 1s, o ritmo da digitação. Uma mudança de
     * configuração (tamanho da imagem, quebra de página) é um clique, não uma tecla: fica
     * marcada aqui para a prévia encurtar a espera. A prévia consome a marca ao reagir à
     * mutação do documento, então ela nunca vale para a mudança seguinte.
     */
    private _discretePreviewChange = false;

    /**
     * Marca a próxima mutação como "clique", não "tecla": a prévia repagina em 150 ms em
     * vez de esperar o silêncio de 1 s. Usado pelo modo IA, a cada alteração aplicada.
     */
    public markDiscretePreviewChange(): void {
        this._discretePreviewChange = true;
    }

    public takeDiscretePreviewChange(): boolean {
        const discrete = this._discretePreviewChange;
        this._discretePreviewChange = false;
        return discrete;
    }

    /** Aplica localmente a data de uma imagem única. */
    public fieldDateUpdated(fieldId: string, date: Date | undefined): void {
        const sessionId = this._currentSession()?._id;
        if (!sessionId || !fieldId) return;

        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items || []).map(item =>
                item._id === fieldId ? { ...item, date } : item
            ),
        }));
    }

    /** Aplica localmente a data de UMA foto de um grupo de imagens. */
    public groupFieldMediaDateUpdated(
        fieldId: string,
        position: number,
        date: Date | undefined
    ): void {
        const sessionId = this._currentSession()?._id;
        if (!sessionId || !fieldId) return;

        this._updateSessionById(sessionId, section => ({
            ...section,
            items: (section.items || []).map(item => {
                if (item._id !== fieldId || !Array.isArray(item.value)) return item;
                return {
                    ...item,
                    value: item.value.map((entry: any, index: number) =>
                        index === position ? { ...entry, date } : entry
                    ),
                };
            }),
        }));
    }

    public fieldUpdated(field: DocumentItemPartial, sessionId: string, sessionSize: number): void {
        const targetSessionId = sessionId ?? this._currentSession()?._id;
        if (!targetSessionId) return;

        this._updateSessionById(targetSessionId, section => ({
            ...section,
            size: sessionSize,
            // Um `field-updated` é mudança de CONTEÚDO, nunca de pasta. Preservamos o
            // vínculo (parentFolderId) local quando o payload não traz um — assim
            // editar um campo nunca o tira de dentro da pasta. A troca de pasta só
            // acontece por create/move/delete.
            items: (section.items || []).map(item =>
                // Eco de um save MAIS ANTIGO que o que já está aqui (vários saves seguidos, os
                // ecos chegando fora de ordem): aplicá-lo voltaria o texto e a revisão, e a
                // próxima edição iria com a base errada. A revisão só cresce.
                item._id === field._id && !isStaleRev(field.rev, item.rev)
                    ? {
                          ...field,
                          parentFolderId: field.parentFolderId ?? item.parentFolderId,
                          // Save de config em voo/recente: o eco (inclusive o do
                          // próprio save) chega atrasado e reverteria o toggle.
                          configs: this._shouldKeepLocalConfigs(field._id)
                              ? item.configs
                              : field.configs,
                      }
                    : item
            ),
        }));
    }

    /**
     * Recarrega os campos de uma seção a partir de um documento recém-buscado.
     *
     * Usado no fallback do Pusher (evento `session-fields-reload`): quando o
     * payload cheio de um evento de campo excede o limite do Pusher, o servidor
     * não envia os dados e pede um refetch. Atualizamos apenas `items` e `size`
     * da seção afetada — preservando seção atual, navegação de pasta e seleção,
     * sem substituir o documento inteiro.
     */
    public reloadSessionFields(
        sessionId: string,
        freshDocument: DocumentEntity,
        excludeFieldIds: string[] = []
    ): void {
        if (!sessionId || !freshDocument) return;

        const freshSession = freshDocument.sessions?.find(section => section._id === sessionId);
        if (!freshSession) return;

        // `excludeFieldIds`: campos removidos localmente que ainda estão em
        // janela de desfazer (o servidor ainda os tem, mas a tela não).
        const freshItems = freshSession.items || [];
        const items = excludeFieldIds.length
            ? freshItems.filter(item => !excludeFieldIds.includes(item._id))
            : freshItems;

        this._updateSessionById(sessionId, section => ({
            ...section,
            size: freshSession.size,
            // Campos com save de config em voo/recente mantêm o `configs` local:
            // o refetch pode ter lido o banco antes do PATCH em andamento.
            items: items.map(freshItem => {
                if (!this._shouldKeepLocalConfigs(freshItem._id)) return freshItem;
                const local = section.items?.find(item => item._id === freshItem._id);
                return local ? { ...freshItem, configs: local.configs } : freshItem;
            }),
        }));
    }

    public fieldsDeleted(fieldsIds: string[], sessionId: string, sessionSize: number): void {
        const targetSessionId = sessionId ?? this._currentSession()?._id;
        if (!targetSessionId) return;

        this._updateSessionById(targetSessionId, section => ({
            ...section,
            size: sessionSize,
            items: (section.items || []).filter(item => !fieldsIds.includes(item._id)),
        }));
    }

    public fieldsMoved(
        currentSessionId: string,
        targetSessionId: string,
        fieldIds: string[],
        targetFolderId?: string
    ): void {
        if (!currentSessionId || !targetSessionId || !fieldIds?.length) return;

        const document = this._document();
        if (!document) return;

        // Mesma seção: o move só re-vincula a pasta de destino (ou solta no nível da
        // seção). Não troca itens de array nem mexe no size.
        if (currentSessionId === targetSessionId) {
            this._updateSessionById(currentSessionId, section => ({
                ...section,
                items: (section.items || []).map(item =>
                    fieldIds.includes(item._id)
                        ? { ...item, parentFolderId: targetFolderId, selected: false }
                        : item
                ),
            }));
            return;
        }

        const currentSession = document.sessions.find(section => section._id === currentSessionId);
        const targetSession = document.sessions.find(section => section._id === targetSessionId);
        if (!currentSession || !targetSession) return;

        const currentItems = currentSession.items || [];
        const targetItems = targetSession.items || [];
        const itemsById = new Map(currentItems.map(item => [item._id, item]));
        const movedItems = fieldIds
            .map(id => itemsById.get(id))
            .filter((item): item is DocumentItemPartial => Boolean(item))
            // Carimba a pasta de destino (ou zera, soltando no nível da seção alvo) e
            // desmarca a seleção — um item movido nunca deve continuar "checado".
            .map(item => ({ ...item, parentFolderId: targetFolderId, selected: false }));

        if (movedItems.length === 0) return;

        const nextCurrentItems = currentItems.filter(item => !fieldIds.includes(item._id));
        const nextTargetItems = [
            ...targetItems.filter(item => !fieldIds.includes(item._id)),
            ...movedItems,
        ];
        const computeSessionSize = (items: DocumentItemPartial[]) =>
            items.reduce((total, item) => total + (Number.isFinite(item.size) ? item.size : 0), 0);

        this._updateSessionById(currentSessionId, section => ({
            ...section,
            items: nextCurrentItems,
            size: computeSessionSize(nextCurrentItems),
        }));

        this._updateSessionById(targetSessionId, section => ({
            ...section,
            items: nextTargetItems,
            size: computeSessionSize(nextTargetItems),
        }));
    }

    public fieldRestored(section: DocumentSessionPartial, field: DocumentItemPartial): void {
        // Procura essa seção no documento
        // Caso não exista, criar a seção com o campo dentro
        // caso exista, adicionar o campo na seção
        // Valida entradas básicas antes de processar
        if (!section || !field) return;

        // Obtém o documento atual em memória
        const document = this._document();
        // Se não houver documento carregado, não há o que atualizar
        if (!document) return;

        // Função auxiliar para recalcular o tamanho total da seção
        const computeSessionSize = (items: DocumentItemPartial[]) =>
            items.reduce((total, item) => total + (Number.isFinite(item.size) ? item.size : 0), 0);

        // Verifica se a seção já existe no documento
        const existingSession = document.sessions.find(item => item._id === section._id);

        // Se a seção não existir, cria a seção com o campo restaurado
        if (!existingSession) {
            // Define lista de itens inicial com o campo restaurado
            const nextItems = [field];
            // Monta a nova seção respeitando tamanho informado ou recalculado
            const nextSession: DocumentSessionPartial = {
                ...section,
                items: nextItems,
                size: Number.isFinite(section.size) ? section.size : computeSessionSize(nextItems),
            };

            // Atualiza o documento adicionando a nova seção
            this._document.update(doc => {
                // Garante que o documento ainda exista no momento da atualização
                if (!doc) return doc;

                return {
                    ...doc,
                    sessions: [...doc.sessions, nextSession],
                };
            });

            // Se não há seção atual, define a recém-criada como atual
            if (!this._currentSession()) this._currentSession.set(nextSession);
            return;
        }

        // Se a seção já existir, atualiza/insere o campo nela
        this._updateSessionById(section._id, currentSession => {
            // Lista de itens atual (fallback para array vazio)
            const currentItems = currentSession.items || [];
            // Verifica se o campo já existe na seção
            const alreadyExists = currentItems.some(item => item._id === field._id);
            // Se existir, substitui; se não, adiciona no final
            const nextItems = alreadyExists
                ? currentItems.map(item => (item._id === field._id ? field : item))
                : [...currentItems, field];

            return {
                ...currentSession,
                // Atualiza metadados da seção com os dados recebidos
                ...section,
                items: nextItems,
                // Mantém tamanho enviado se válido, senão recalcula
                size: Number.isFinite(section.size) ? section.size : computeSessionSize(nextItems),
            };
        });
    }

    /**
     * Aplica a nova ordem dos campos na seção (por exemplo, vinda de colaboração em tempo real).
     * @param sessionId ID da seção
     * @param fieldsIds IDs dos campos na ordem desejada
     */
    public fieldsReordered(sessionId: string, fieldsIds: string[]): void {
        if (!sessionId || !fieldsIds?.length) return;

        this._updateSessionById(sessionId, section => {
            const currentItems = section.items || [];
            const byId = new Map(currentItems.map(item => [item._id, item]));

            const ordered: DocumentItemPartial[] = [];
            for (const id of fieldsIds) {
                const item = byId.get(id);
                if (item) ordered.push(item);
            }
            const orderedIds = new Set(fieldsIds);
            for (const item of currentItems) {
                if (!orderedIds.has(item._id)) ordered.push(item);
            }

            return {
                ...section,
                items: ordered,
            };
        });
    }

    // #endregion
}

/** A revisão que chegou é anterior à local (as duas conhecidas). */
function isStaleRev(incoming: number | null | undefined, current: number | null | undefined): boolean {
    return incoming != null && current != null && incoming < current;
}
